"use client";

import React from "react";
import { Activity, ArrowRight, CircleCheck as CheckCircle2, Cpu, FileText, Layers, ShieldCheck } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
const highlights = [{
  title: "ATEX / IECEx Certified",
  description: "Rated for safe operation in explosive and hazardous manufacturing zones.",
  icon: ShieldCheck
}, {
  title: "Solid-State LVF Engine",
  description: "Ultra-compact Linear Variable Filter optics with no moving parts for high stability.",
  icon: Cpu
}, {
  title: "Continuous In-line Analysis",
  description: "Real-time monitoring for blend uniformity, drying endpoints, and reaction progression.",
  icon: Activity
}, {
  title: "Factory Integration",
  description: "Standard industrial protocols with OPC UA and Modbus connectivity to SCADA systems.",
  icon: Layers
}];
const technicalSpecs = [{
  label: "Spectral Range",
  value: "950 – 1650 nm"
}, {
  label: "Detector",
  value: "128-pixel uncooled InGaAs"
}, {
  label: "Enclosure Rating",
  value: "IP65 / IP67 Stainless Steel"
}, {
  label: "Power Supply",
  value: "24 VDC industrial input"
}];
export default function MicroNir() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 16
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5,
        ease: "easeOut"
      }
    }
  };
  return <section id="heromicronirpatux" data-nav="light" className="relative overflow-hidden bg-background py-16 sm:py-20 lg:py-24">
      {/* Subtle technical background grid */}
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(to_right,hsl(210_20%_88%/0.35)_1px,transparent_1px),linear-gradient(to_bottom,hsl(210_20%_88%/0.35)_1px,transparent_1px)] bg-[size:40px_40px] [mask-image:radial-gradient(ellipse_60%_50%_at_50%_0%,#000_70%,transparent_100%)]" aria-hidden="true" />

      <div className="page-container relative">
        <motion.div variants={containerVariants} initial="hidden" animate="visible" className="grid gap-12 lg:grid-cols-12 lg:items-center lg:gap-16">
          {/* Left Column: Hero Copy */}
          <div className="lg:col-span-7">
            <motion.div variants={itemVariants} className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider text-secondary-foreground">
                Process Analytical Technology (PAT)
              </Badge>
              <span className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-2.5 py-0.5 text-xs text-muted-foreground">
                <span className="size-1.5 rounded-full bg-primary" />
                MicroNIR PAT-Ux
              </span>
            </motion.div>

            <motion.h1 variants={itemVariants} className="mt-6 font-default text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl">
              Real-time NIR process monitoring for regulated manufacturing
            </motion.h1>

            <motion.p variants={itemVariants} className="mt-5 text-base leading-relaxed text-muted-foreground sm:text-lg">
              The MicroNIR PAT-Ux is an ultra-compact, non-invasive near-infrared spectrometer engineered for direct installation on process vessels, blenders, and fluid bed dryers in hazardous and regulated production environments.
            </motion.p>

            {/* CTA Group */}
            <motion.div variants={itemVariants} className="mt-8 flex flex-wrap items-center gap-4">
              <Button asChild size="lg" className="shadow-sm">
                <Link to="/BookADemoPage">
                  Book a Demonstration
                  <ArrowRight className="size-4" />
                </Link>
              </Button>
              <Button asChild variant="outline" size="lg">
                <Link to="/DiscussYourApplicationPage">
                  Discuss Your Application
                </Link>
              </Button>
            </motion.div>

            {/* Quick Spec Bar */}
            <motion.div variants={itemVariants} className="mt-10 rounded-lg border border-border bg-secondary/40 p-4 sm:p-5">
              <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
                {technicalSpecs.map((spec, i) => <div key={spec.label} data-index={i} className="space-y-1">
                    <p className="text-xs font-medium text-muted-foreground">{spec.label}</p>
                    <p className="text-sm font-semibold text-foreground">{spec.value}</p>
                  </div>)}
              </div>
            </motion.div>
          </div>

          {/* Right Column: Visual Component Card */}
          <motion.div variants={itemVariants} className="lg:col-span-5">
            <div className="relative rounded-xl border border-border bg-card p-6 shadow-md sm:p-8">
              {/* Decorative Header */}
              <div className="flex items-center justify-between border-b border-border pb-4">
                <div className="flex items-center gap-2">
                  <div className="size-3 rounded-full bg-chart-1" />
                  <div className="size-3 rounded-full bg-border" />
                  <div className="size-3 rounded-full bg-border" />
                  <span className="ml-2 font-mono text-xs text-muted-foreground">PAT-Ux Spectrometer Unit</span>
                </div>
                <Badge variant="outline" className="font-mono text-[11px] text-muted-foreground">
                  Ready
                </Badge>
              </div>

              {/* Product Visual Container */}
              <div className="relative my-6 overflow-hidden rounded-lg border border-border bg-secondary/30 p-6 sm:p-8">
                <div className="flex aspect-4/3 items-center justify-center">
                  <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_patux_spectrometer.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_patux_spectrometer.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1776299/img/micronir_patux_spectrometer.png-wvc-sizes" alt="MicroNIR PAT-Ux Process Analytical Technology Spectrometer" loading="lazy" decoding="async" className="h-full w-full object-contain transition-transform duration-300 hover:scale-105" />
                </div>
                <div className="absolute right-3 bottom-3 rounded-md bg-background/90 px-2.5 py-1 text-[11px] font-medium text-foreground shadow-2xs backdrop-blur-xs">
                  Hazardous Location (Zone 0/20 &amp; Class I/II Div 1)
                </div>
              </div>

              {/* Key Verification Checklist */}
              <div className="space-y-2.5 text-xs text-muted-foreground">
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 shrink-0 text-chart-3" />
                  <span>21 CFR Part 11 and USP &lt;1119&gt; / EP 2.2.40 compliance ready</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle2 className="size-4 shrink-0 text-chart-3" />
                  <span>Direct flange and sanitary tri-clamp process mounting interfaces</span>
                </div>
              </div>

              {/* Secondary Link */}
              <div className="mt-6 pt-4 border-t border-border flex items-center justify-between text-xs">
                <span className="text-muted-foreground">Detailed application note</span>
                <Link to="/ProcessAnalyticalTechnologyPage" className="font-medium text-primary hover:underline inline-flex items-center gap-1">
                  <FileText className="size-3.5" />
                  Explore PAT Solutions
                </Link>
              </div>
            </div>
          </motion.div>
        </motion.div>

        {/* Feature Grid Section */}
        <div className="mt-16 sm:mt-20 border-t border-border pt-12 sm:pt-16">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
            {highlights.map((item, i) => {
            const IconComponent = item.icon;
            return <div key={item.title} data-index={i} className="rounded-lg border border-border bg-card p-5 shadow-xs transition-shadow duration-200 hover:shadow-md">
                  <div className="flex size-10 items-center justify-center rounded-md bg-secondary text-secondary-foreground">
                    <IconComponent className="size-5" />
                  </div>
                  <h2 className="mt-4 font-default text-base font-semibold text-card-foreground">
                    {item.title}
                  </h2>
                  <p className="mt-2 text-sm leading-normal text-muted-foreground">
                    {item.description}
                  </p>
                </div>;
          })}
          </div>
        </div>
      </div>
    </section>;
}